// src/pages/common/ModulePage.js
// 占位菜单通用骨架页：依据当前路由从 modulePages 取配置，统一渲染骨架。
// 未配置的路径回退到 ModulePlaceholder。
//
// 交互说明（纯前端本地行为，后端未接入）：
//  - 筛选栏受控，值变化即时驱动表格 / 卡片 / 列表区块的本地过滤；
//  - 若页面同时提供「查询」按钮，则以「查询」为准提交筛选条件；「重置」清空所有条件；
//  - 「刷新」重新挂载内容区（图表重绘）并给出提示；
//  - 「导出」把当前（过滤后）主表格导出为 CSV 下载；
//  - 「全屏展示」切换浏览器全屏；「自定义布局」切换内容区栅格；
//  - 「进入三维场景」打开三维骨架预览；大屏页标题连击 5 下进入大屏配置。
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { message, Drawer, Radio, Space, Button, Modal, Tag } from 'antd';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import PageSkeleton from '../../components/PageSkeleton';
import BlockRenderer from '../../components/blocks/BlockRenderer';
import ModulePlaceholder from '../../components/ModulePlaceholder';
import { findMenu, pick } from '../../config/menuConfig';
import { MODULE_PAGES } from '../../config/modulePages';
import { STATIONS } from '../../config/stationData';
import { exportTableToExcel } from '../../utils/exportExcel';

// ---------------- 筛选 / 过滤工具 ----------------
const isActive = (v) => v !== undefined && v !== null && v !== ''
  && !(Array.isArray(v) && v.length === 0);

// 把任意值归一化为可检索字符串（含 dayjs 对象）
const toStr = (v) => {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.map(toStr).join(' ');
  if (typeof v === 'object') {
    if (typeof v.format === 'function') return v.format('YYYY-MM-DD');
    return '';
  }
  return String(v);
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}/;

// 单行是否命中全部有效筛选条件
const rowMatches = (row, filters, filterDefs) => {
  const hay = Object.values(row).map(toStr).join(' ').toLowerCase();
  for (const f of filterDefs) {
    if (f.noRowFilter) continue; // 页面级切换控件（如电池分析「站点」），不参与行文本过滤
    const val = filters[f.key];
    if (!isActive(val)) continue;
    if (f.type === 'daterange') {
      const dates = Object.values(row).map(toStr).filter((s) => DATE_RE.test(s));
      if (!dates.length) continue; // 无日期字段的行不参与时间过滤
      const s = toStr(val[0]).slice(0, 10);
      const e = toStr(val[1]).slice(0, 10);
      if (!dates.some((d) => { const dd = d.slice(0, 10); return dd >= s && dd <= e; })) return false;
    } else if (f.type === 'month') {
      const dates = Object.values(row).map(toStr).filter((s) => DATE_RE.test(s));
      if (!dates.length) continue;
      const m = toStr(val).slice(0, 7);
      if (!dates.some((d) => d.slice(0, 7) === m)) return false;
    } else if (f.type === 'select') {
      const opt = (f.options || []).find((o) => o.value === val);
      const needle = (opt ? toStr(opt.label) : toStr(val)).toLowerCase();
      if (!hay.includes(needle)) return false;
    } else {
      // search 及其它
      if (!hay.includes(toStr(val).toLowerCase())) return false;
    }
  }
  return true;
};

// 递归过滤区块内的表格 / 卡片 / 列表数据
const filterBlocks = (blocks, filters, filterDefs) => {
  if (!filterDefs.some((f) => isActive(filters[f.key]))) return blocks;
  return (blocks || []).map((b) => {
    if (!b) return b;
    if (b.type === 'table' && Array.isArray(b.rows)) {
      return { ...b, rows: b.rows.filter((r) => rowMatches(r, filters, filterDefs)) };
    }
    if ((b.type === 'cards' || b.type === 'list') && Array.isArray(b.items)) {
      return { ...b, items: b.items.filter((it) => rowMatches(it, filters, filterDefs)) };
    }
    if (b.type === 'tabs' && Array.isArray(b.items)) {
      return { ...b, items: b.items.map((it) => ({ ...it, blocks: filterBlocks(it.blocks || [], filters, filterDefs) })) };
    }
    if (b.type === 'row' && Array.isArray(b.blocks)) {
      return { ...b, blocks: filterBlocks(b.blocks, filters, filterDefs) };
    }
    return b;
  });
};

// 递归收集页面内的表格区块（用于导出）
const collectTables = (blocks, acc = []) => {
  (blocks || []).forEach((b) => {
    if (!b) return;
    if (b.type === 'table') acc.push(b);
    else if (b.type === 'tabs') (b.items || []).forEach((it) => collectTables(it.blocks, acc));
    else if (b.type === 'row') collectTables(b.blocks, acc);
  });
  return acc;
};

const downloadCsv = (table, title) => {
  // 缺陷 90：导出按钮需真正导出 Excel（此前仅生成 CSV / 或只弹提示）
  exportTableToExcel({ title, columns: table.columns, rows: table.rows });
};

const LAYOUT_OPTIONS = [
  { value: 'standard', label: '标准（图表更宽，两列）' },
  { value: 'even', label: '等宽两列' },
  { value: 'triple', label: '紧凑三列' },
  { value: 'single', label: '单列堆叠' },
];

// 自动刷新时间标签（doc01 2.6 p16：实时运行列表每 5 秒刷新）
const fmtClock = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

const ModulePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const meta = findMenu(location.pathname);
  const cfg = MODULE_PAGES[location.pathname];

  const filterDefs = (cfg && cfg.filters) || [];
  const actions = (cfg && cfg.actions) || [];
  const hasQuery = actions.some((a) => a.key === 'query');
  const pageTitle = meta ? pick(meta.label, lang) : '';

  const [values, setValues] = useState(() => {
    const init = {};
    filterDefs.forEach((f) => { if (f.defaultValue !== undefined) init[f.key] = f.defaultValue; });
    return init;
  });
  const [applied, setApplied] = useState({});
  const [version, setVersion] = useState(0);
  // 逐模块时间范围（doc01：各模块可单独设置时间范围，支持自定义）
  const [sectionTimes, setSectionTimes] = useState({});

  // 数据大屏配置（doc01 2.1）
  const [screenOpen, setScreenOpen] = useState(false);
  const [screenCfg, setScreenCfg] = useState({ map: 'china', source: 'real' });
  const [screenApplied, setScreenApplied] = useState({ map: 'china', source: 'real' });
  // 数据大屏地图模块的隐藏站点（不计入统计，doc01 2.1）
  const [screenHidden, setScreenHidden] = useState([]);
  // 自定义布局（doc01 2.2 / 2.3）
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [layout, setLayout] = useState('standard');
  const [layoutDraft, setLayoutDraft] = useState('standard');
  // 三维场景骨架（doc01 2.5）
  const [sceneOpen, setSceneOpen] = useState(false);
  // 是否处于全屏（驱动「全屏展示 / 退出全屏」按钮状态）
  const [isFullscreen, setIsFullscreen] = useState(false);
  // 原生全屏被浏览器策略限制时的页面内全屏兜底，保证按钮状态仍能正常切换
  const [pseudoFull, setPseudoFull] = useState(false);
  // 标题连击计数
  const hitsRef = useRef(0);
  const titleTimer = useRef(null);
  // 实时数据自动刷新（doc01 2.6 p16：列表每 5 秒刷新实时数据）
  const [lastRefresh, setLastRefresh] = useState(() => new Date());
  // 缺陷 125：自动刷新只更新数值 / 时间戳（autoTick），不触发内容区整体重挂载，
  // 避免小时级图表被每 5 秒重绘；仅当筛选项 / 数据真正变化（version）时才重绘图表。
  const [autoTick, setAutoTick] = useState(0);

  useEffect(() => {
    const sec = (cfg && cfg.autoRefresh) || 0;
    if (!sec) return undefined;
    const t = setInterval(() => {
      setAutoTick((v) => v + 1);
      setLastRefresh(new Date());
    }, sec * 1000);
    return () => clearInterval(t);
  }, [cfg]);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  if (!meta || !cfg) return <ModulePlaceholder />;

  // 无「查询」按钮的页面：筛选即时生效；有「查询」按钮的页面：需点击查询提交
  const effectiveFilters = hasQuery ? applied : values;

  // 支持 blocks 为函数：可依据当前筛选值动态生成区块（如三维可视化随站点切换、大屏随隐藏站点联动）
  const rawBlocks = typeof cfg.blocks === 'function' ? cfg.blocks(effectiveFilters, screenApplied, screenHidden) : (cfg.blocks || []);

  const handleFilterChange = (key, v) => {
    const next = { ...values, [key]: v };
    setValues(next);
    if (!hasQuery) setApplied(next);
  };

  const handleTitleClick = () => {
    if (!cfg.screenConfig) return;
    hitsRef.current += 1;
    clearTimeout(titleTimer.current);
    titleTimer.current = setTimeout(() => { hitsRef.current = 0; }, 1200);
    if (hitsRef.current >= 5) {
      hitsRef.current = 0;
      setScreenCfg(screenApplied);
      setScreenOpen(true);
    }
  };

  const toggleFullscreen = () => {
    if (isFullscreen) {
      (document.exitFullscreen || document.webkitExitFullscreen || (() => {})).call(document);
      setIsFullscreen(false);
      return;
    }
    if (pseudoFull) {
      setPseudoFull(false);
      return;
    }
    const el = document.documentElement;
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    if (req) {
      Promise.resolve(req.call(el))
        .then(() => { setIsFullscreen(true); message.success('已进入全屏展示'); })
        .catch(() => { setPseudoFull(true); message.success('已进入全屏展示（页面内全屏）'); });
    } else {
      setPseudoFull(true);
      message.success('已进入全屏展示（页面内全屏）');
    }
  };

  const applyLayout = () => {
    setLayout(layoutDraft);
    message.success('布局已应用');
    setLayoutOpen(false);
  };

  const handleAction = (action) => {
    const a = typeof action === 'string' ? { label: action } : (action || {});
    switch (a.key) {
      case 'query':
        setApplied({ ...values });
        setVersion((v) => v + 1);
        message.success('已按当前筛选条件查询');
        break;
      case 'reset':
        setValues({});
        setApplied({});
        setVersion((v) => v + 1);
        message.success('筛选条件已重置');
        break;
      case 'refresh':
        setVersion((v) => v + 1);
        message.success('数据已刷新');
        break;
      case 'enterMonitor': {
        // 进入单站监控：携带与当前「站点名称」筛选匹配的站点，保证跳转后站点一致
        const kw = toStr(effectiveFilters[a.stationKey]).trim();
        const hit = (kw && STATIONS.find((s) => s.name.includes(kw))) || STATIONS[0];
        navigate(`/app/monitor/station?station=${hit.id}`);
        break;
      }
      case 'enterStation': {
        // 评审 56：表格行内「进入监控」按钮，直接绑定该行的站点
        if (a.id) navigate(`/app/monitor/station?station=${a.id}`);
        break;
      }
      case 'export': {
        const tables = collectTables(rawBlocks);
        if (!tables.length) { message.info('当前页面暂无可导出的表格数据'); break; }
        downloadCsv(tables[0], pageTitle || 'export');
        message.success(`已导出「${tables[0].title || pageTitle}」为 Excel`);
        break;
      }
      case 'full':
        toggleFullscreen();
        break;
      case 'hideSite': {
        // 大屏地图模块：隐藏 / 取消隐藏站点（隐藏后不计入聚合统计）
        setScreenHidden((prev) => (prev.includes(a.id) ? prev.filter((x) => x !== a.id) : [...prev, a.id]));
        break;
      }
      case 'resetHidden':
        setScreenHidden([]);
        message.success('已恢复全部站点显示');
        break;
      case 'custom':
        setLayoutDraft(layout);
        setLayoutOpen(true);
        break;
      case 'enter':
        setSceneOpen(true);
        break;
      default:
        message.info(`${a.label || '操作'}：接口待接入`);
    }
  };

  // 为带 timeFilter 的分区注入当前时间值与回调（各模块独立，互不影响）
  const annotateSections = (list) => (list || []).map((b) => (b && b.type === 'section' && b.timeFilter
    ? {
      ...b,
      timeValue: sectionTimes[b.key] || '近7日',
      onTimeChange: (v) => setSectionTimes((s) => ({ ...s, [b.key]: v })),
    }
    : b));

  // 各模块时间范围影响其下标记 timeRange 的图表数据（演示：按范围系数聚合，自定义范围按原值）
  const RANGE_FACTOR = { 近7日: 1, 当月: 4.2, 当年: 12.5, 全部: 20 };
  const scaleRows = (rows, factor) => (rows || []).map((r) => {
    const o = { ...r };
    Object.keys(o).forEach((k) => { if (typeof o[k] === 'number') o[k] = Math.round(o[k] * factor); });
    return o;
  });
  const applyRanges = (list) => {
    let range = '近7日';
    const walk = (items) => (items || []).map((b) => {
      if (!b) return b;
      if (b.type === 'section') {
        if (b.timeFilter) range = b.timeValue || '近7日';
        return b;
      }
      if (b.type === 'row') return { ...b, blocks: walk(b.blocks) };
      if (b.type === 'tabs') return { ...b, items: (b.items || []).map((it) => ({ ...it, blocks: walk(it.blocks) })) };
      if (b.timeRange && Array.isArray(b.data)) {
        // 范围只缩放数据；不把长日期串写入 extra，避免仅一侧标题换行导致并列卡片图区错位
        const shortRange = (range || '').includes('~') ? '自定义' : range;
        return {
          ...b,
          data: scaleRows(b.data, RANGE_FACTOR[range] || 1),
          extra: `${b.extra ? `${b.extra} · ` : ''}${shortRange}`,
        };
      }
      return b;
    });
    return walk(list);
  };

  const blocks = filterBlocks(applyRanges(annotateSections(rawBlocks)), effectiveFilters, filterDefs);

  // 全屏按钮文案随当前全屏状态切换（doc：全屏后按钮应显示「退出全屏」）
  const actionsView = actions.map((a) => (a.key === 'full'
    ? {
      ...a,
      label: (isFullscreen || pseudoFull) ? '退出全屏' : (a.label || '全屏展示'),
      icon: (isFullscreen || pseudoFull) ? <FullscreenExitOutlined /> : <FullscreenOutlined />,
    }
    : a));

  const headExtra = cfg.screenConfig ? (
    <Space size={6}>
      <Tag color="cyan">地图：{screenApplied.map === 'world' ? '世界地图' : '中国地图'}</Tag>
      <Tag color="blue">数据源：{screenApplied.source === 'custom' ? '自定义数据' : '真实数据'}</Tag>
    </Space>
  ) : undefined;

  return (
    <div className={pseudoFull ? 'page-pseudo-full' : undefined}>
      <PageSkeleton
        title={pageTitle}
        desc={pick(meta.desc, lang)}
        crumb={[pick(meta.groupLabel, lang), pick(meta.sectionLabel, lang), pick(meta.label, lang)]}
        filters={filterDefs}
        actions={actionsView}
        extra={headExtra}
        filterValues={values}
        onFilterChange={handleFilterChange}
        onAction={handleAction}
        onTitleClick={cfg.screenConfig ? handleTitleClick : undefined}
      >
        {cfg.autoRefresh ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end',
            marginBottom: 8, color: 'rgba(255,255,255,0.45)', fontSize: 12,
          }}
          >
            <Tag color="cyan">每 {cfg.autoRefresh} 秒自动刷新</Tag>
            <span>最后刷新 {fmtClock(lastRefresh)}</span>
          </div>
        ) : null}
        <div className={`mod-body layout-${layout}`} key={version} data-tick={autoTick}>
          {blocks.map((b, i) => (
            <BlockRenderer key={i} block={b} onAction={handleAction} />
          ))}
        </div>
      </PageSkeleton>

      {/* 大屏配置：标题连击 5 下进入 */}
      <Drawer
        className="cfg-drawer"
        title="数据大屏配置"
        open={screenOpen}
        onClose={() => setScreenOpen(false)}
        size={420}
        extra={(
          <Space>
            <Button onClick={() => { setScreenApplied({ ...screenCfg }); message.success('已预览自定义大屏'); }}>预览</Button>
            <Button type="primary" onClick={() => { setScreenApplied({ ...screenCfg }); message.success('大屏配置已应用'); setScreenOpen(false); }}>应用</Button>
          </Space>
        )}
      >
        <div className="cfg-block">
          <div className="cfg-label">地图</div>
          <Radio.Group value={screenCfg.map} onChange={(e) => setScreenCfg((s) => ({ ...s, map: e.target.value }))}>
            <Radio value="china">中国地图</Radio>
            <Radio value="world">世界地图</Radio>
          </Radio.Group>
        </div>
        <div className="cfg-block">
          <div className="cfg-label">数据源</div>
          <Radio.Group value={screenCfg.source} onChange={(e) => setScreenCfg((s) => ({ ...s, source: e.target.value }))}>
            <Radio value="real">真实数据</Radio>
            <Radio value="custom">自定义数据</Radio>
          </Radio.Group>
          <div className="cfg-hint">默认使用真实数据；修改后须点击「应用」才会生效，可先点击「预览」查看自定义大屏。</div>
        </div>
      </Drawer>

      {/* 自定义布局 */}
      <Drawer
        className="cfg-drawer"
        title="自定义布局"
        open={layoutOpen}
        onClose={() => setLayoutOpen(false)}
        size={380}
        extra={(
          <Space>
            <Button onClick={() => setLayoutDraft('standard')}>重置</Button>
            <Button type="primary" onClick={applyLayout}>应用</Button>
          </Space>
        )}
      >
        <div className="cfg-label">展示样式</div>
        <Radio.Group
          value={layoutDraft}
          onChange={(e) => setLayoutDraft(e.target.value)}
          style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}
        >
          {LAYOUT_OPTIONS.map((o) => <Radio key={o.value} value={o.value}>{o.label}</Radio>)}
        </Radio.Group>
      </Drawer>

      {/* 三维场景骨架 */}
      <Modal
        title="三维场景"
        open={sceneOpen}
        onCancel={() => setSceneOpen(false)}
        width="92%"
        style={{ top: 16 }}
        footer={<Button type="primary" onClick={() => setSceneOpen(false)}>关闭</Button>}
      >
        <div className="scene-stage">
          <div className="scene-level">
            <div className="scene-node site">站点</div>
            <div className="scene-arrow">→</div>
            <div className="scene-node box">箱体</div>
            <div className="scene-arrow">→</div>
            <div className="scene-node device">设备</div>
          </div>
          <div className="scene-hint">
            层级：站点 → 箱体 → 设备。设备层可查看实时运行数据与今日告警，电池簇可查看簇内所有单体的电压 / 温度 / SOC / SOH。
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ModulePage;